import * as XLSX from 'xlsx'

export interface KOTImportLine {
   sku: string
   qty: number
}

export interface KOTImportResult {
   lines: KOTImportLine[]
   metadata?: { period?: string }
}

export interface KOTValidationError {
   row: number
   message: string
}

/**
 * Valide les lignes importées d'un KOT.
 * Retourne un tableau d'erreurs (vide si tout est OK).
 *
 * @param options.skuExists - Si fourni, une erreur est émise pour tout SKU absent de la base.
 */
export function validateKOTLines(
   lines: { sku?: string; qty?: number }[],
   options?: { skuExists?: (sku: string) => boolean }
): KOTValidationError[] {
   const errors: KOTValidationError[] = []
   const seenSkus = new Set<string>()

   for (let i = 0; i < lines.length; i++) {
      const line = lines[i]
      const row = i + 2 // +2 car ligne 1 = header

      if (!line.sku || String(line.sku).trim() === '') {
         errors.push({ row, message: `Ligne ${row} : SKU manquant` })
         continue
      }

      const sku = String(line.sku).trim()

      if (seenSkus.has(sku)) {
         errors.push({ row, message: `Ligne ${row} : SKU "${sku}" en doublon` })
         continue
      }
      seenSkus.add(sku)

      const qty = Number(line.qty)
      if (isNaN(qty) || !Number.isInteger(qty) || qty <= 0) {
         errors.push({ row, message: `Ligne ${row} : quantité invalide (entier > 0 attendu)` })
      }

      if (options?.skuExists && !options.skuExists(sku)) {
         errors.push({ row, message: `Ligne ${row} : SKU "${sku}" inconnu` })
      }
   }

   return errors
}

/**
 * Exporte les lignes d'un KOT en CSV et déclenche le téléchargement.
 */
export function exportKOTToCSV(
   lines: { sku: string; qty: number }[],
   getModelLabel: (sku: string) => string,
   filename: string = 'kot-export.csv'
) {
   const rows = lines.map((l) => ({
      sku: l.sku,
      'libelle (facultatif)': getModelLabel(l.sku),
      qty: l.qty,
   }))

   const ws = XLSX.utils.json_to_sheet(rows)
   const csv = XLSX.utils.sheet_to_csv(ws, { FS: ';' })

   const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }) // BOM pour Excel
   triggerDownload(blob, filename)
}

/**
 * Exporte les lignes d'un KOT en Excel (.xlsx) et déclenche le téléchargement.
 * Si metadata est fourni, crée une feuille "Metadata" avec la période.
 */
export function exportKOTToExcel(
   lines: { sku: string; qty: number }[],
   getModelLabel: (sku: string) => string,
   metadata?: { period?: string },
   filename: string = 'kot-export.xlsx'
) {
   const headers = ['sku', 'libelle (facultatif)', 'qty']
   const rows = lines.map((l) => ({
      sku: l.sku,
      'libelle (facultatif)': getModelLabel(l.sku),
      qty: l.qty,
   }))

   const wb = XLSX.utils.book_new()
   const ws = rows.length === 0
      ? XLSX.utils.aoa_to_sheet([headers])
      : XLSX.utils.json_to_sheet(rows, { header: headers })
   XLSX.utils.book_append_sheet(wb, ws, 'Lignes')

   if (metadata?.period) {
      const wsMeta = XLSX.utils.json_to_sheet([{ period: metadata.period }])
      XLSX.utils.book_append_sheet(wb, wsMeta, 'Metadata')
   }

   const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
   const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
   triggerDownload(blob, filename)
}

/**
 * Parse un fichier CSV ou Excel et retourne les lignes + métadonnées optionnelles.
 */
export async function importKOTFile(file: File): Promise<KOTImportResult> {
   const data = await file.arrayBuffer()
   const wb = XLSX.read(data)

   // Lire la première feuille (lignes)
   const firstSheetName = wb.SheetNames[0]
   const ws = wb.Sheets[firstSheetName]
   const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws)

   const lines: KOTImportLine[] = rawRows
      .filter((row) => {
         // Ignorer les lignes complètement vides
         const sku = row['sku'] ?? row['SKU'] ?? row['Sku']
         return sku !== undefined && sku !== null && String(sku).trim() !== ''
      })
      .map((row) => {
         const sku = String(row['sku'] ?? row['SKU'] ?? row['Sku'] ?? '').trim().toUpperCase()
         const qty = Number(row['qty'] ?? row['QTY'] ?? row['Qty'] ?? 0)
         return { sku, qty }
      })

   // Lire metadata si feuille "Metadata" présente
   let metadata: KOTImportResult['metadata'] = undefined
   if (wb.SheetNames.includes('Metadata')) {
      const wsMeta = wb.Sheets['Metadata']
      const metaRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(wsMeta)
      if (metaRows.length > 0) {
         const period = metaRows[0]['period'] ?? metaRows[0]['Period'] ?? metaRows[0]['PERIOD']
         if (period) {
            metadata = { period: String(period) }
         }
      }
   }

   return { lines, metadata }
}

function triggerDownload(blob: Blob, filename: string) {
   const url = URL.createObjectURL(blob)
   const a = document.createElement('a')
   a.href = url
   a.download = filename
   document.body.appendChild(a)
   a.click()
   document.body.removeChild(a)
   URL.revokeObjectURL(url)
}
